import { type AppLocale } from '@/i18n/locales'
import { noticeText } from './speech-text'
import { BuffSpeechPlayer } from './speech-player'
import { buffCountdownFresh, observationFresh, reminderSettings, type BuffDefinition, type BuffObservation, type BuffPreferences } from './types'

export interface BuffNotice { id: string; cycle: number; timerSource?: 'manual'; kind: 'nearing'; name: string; order: number; speech: boolean; visual: boolean; at: number }
export class BuffReminderRules {
  private cycles = new Map<string, { cycle: number; timerSource?: 'manual'; near: boolean }>()
  reset(): void { this.cycles.clear() }
  evaluate(observations: BuffObservation[], definitions: BuffDefinition[], preferences: BuffPreferences, now: number): BuffNotice[] {
    const notices: BuffNotice[] = []
    for (const observation of observations) {
      const definition = definitions.find(item => item.id === observation.id && item.enabled)
      if (!definition || observation.presence === 'unknown' || !observationFresh(observation, now)) continue
      const rule = reminderSettings(definition, preferences)
      let cycle = this.cycles.get(definition.id)
      if (!cycle || cycle.cycle !== observation.cycle || cycle.timerSource !== observation.timerSource) { cycle = { cycle: observation.cycle, timerSource: observation.timerSource, near: false }; this.cycles.set(definition.id, cycle) }
      let kind: BuffNotice['kind'] | null = null
      if (observation.presence === 'present' && !cycle.near && rule.nearing && remainingUpperSeconds(observation, now) !== null && remainingUpperSeconds(observation, now)! <= rule.threshold) {
        cycle.near = true; kind = 'nearing'
      }
      if (kind) notices.push({ id: definition.id, cycle: observation.cycle, ...(observation.timerSource ? { timerSource: observation.timerSource } : {}), kind, name: definition.name, order: definition.order, speech: rule.speech, visual: rule.visual, at: now })
    }
    return notices
  }
}
export function currentNotice(notice: BuffNotice, observations: BuffObservation[], now: number): boolean {
  const observation = observations.find(item => item.id === notice.id)
  return !!observation && observationFresh(observation, now) && observation.cycle === notice.cycle && observation.timerSource === notice.timerSource
    && observation.presence === 'present' && observation.seconds !== null && remainingUpperSeconds(observation, now) !== null
}
export { noticeText } from './speech-text'
export function remainingUpperSeconds(observation: BuffObservation, now: number): number | null {
  return observation.timeReason === 'number-conflict-pending' || observation.timeReason === 'renewal-confirming' || observation.upperSeconds === null || !buffCountdownFresh(observation, now)
    ? null : Math.max(0, observation.upperSeconds - Math.max(0, now - observation.capturedAt) / 1000)
}
export interface BuffSpeechEvent { type: 'queued' | 'paused' | 'resumed' | 'cancelled' | 'started' | 'completed' | 'failed'; key: string; at: number; reason?: string }
type Delivery = { notice: BuffNotice; status: 'pending' | 'paused' | 'speaking' | 'failed' }
const noticeKey = (notice: BuffNotice) => `${notice.id}:${notice.timerSource ? notice.timerSource + ':' : ''}${notice.cycle}:${notice.kind}`
export class BuffSpeechQueue {
  private queue = new Map<string, Delivery>()
  private terminal = new Set<string>()
  private timer: ReturnType<typeof setTimeout> | null = null
  private speaking: { cancel: () => void; preview: boolean; started: boolean } | null = null
  private previewToken = 0
  constructor(private readonly current: () => { observations: BuffObservation[]; preferences: BuffPreferences; locale: AppLocale; definitions?: BuffDefinition[] }, private readonly failed: (message: string) => void, private readonly audit: (event: BuffSpeechEvent) => void = () => {}, private readonly now: () => number = Date.now, private readonly playbackChanged: (playing: boolean) => void = () => {}, private readonly previewFailed: (message: string) => void = failed, readonly player = new BuffSpeechPlayer(), private readonly preparingChanged: (preparing: boolean) => void = () => {}) {}
  private log(type: BuffSpeechEvent['type'], key: string, reason?: string) { this.audit({type,key,at:this.now(),reason}) }
  private finish(key: string, type: 'cancelled' | 'completed', reason?: string) {
    this.queue.delete(key); this.terminal.add(key)
    if (this.terminal.size > 512) this.terminal.delete(this.terminal.values().next().value!)
    this.log(type,key,reason)
  }
  private schedule() {
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = setTimeout(() => { this.timer = null; this.flush() }, 0)
  }
  enqueue(notices: BuffNotice[]): void {
    let added = false
    for (const notice of notices) {
      const key = noticeKey(notice)
      if (!notice.speech || this.terminal.has(key) || this.queue.has(key)) continue
      this.queue.set(key,{notice,status:'pending'}); this.log('queued',key); added = true
    }
    if (added) this.cancelPendingPreview()
    if (added && this.timer === null) this.schedule()
  }
  reconcile(): void {
    const state = this.current(), now = this.now()
    let resumed = false
    for (const [key, entry] of this.queue) {
      if (entry.status === 'speaking') continue // Finish the current sentence; revalidate the next item separately.
      const notice = entry.notice
      const definition = state.definitions?.find(item => item.id === notice.id && item.enabled)
      const rule = definition ? reminderSettings(definition, state.preferences) : null
      const observation = state.observations.find(item => item.id === notice.id)
      let cancel = ''
      if (state.definitions && (!definition || !rule?.speech)) cancel = 'disabled'
      else if (rule && !rule.nearing) cancel = 'event-disabled'
      else if (observation && (observation.cycle !== notice.cycle || observation.timerSource !== notice.timerSource) || !observation && notice.timerSource === 'manual') cancel = 'cycle-changed'
      if (cancel) { this.finish(key,'cancelled',cancel); continue }
      if (definition && rule) entry.notice = {...notice,name:definition.name,order:definition.order}
      const fresh = observation && observationFresh(observation, now)
      if (!fresh || observation.presence === 'unknown' || observation.presence === 'present' && (observation.seconds === null || remainingUpperSeconds(observation,now) === null)) {
        if (entry.status !== 'paused' && entry.status !== 'failed') { entry.status='paused'; this.log('paused',key,'evidence-unavailable') }
        continue
      }
      if (observation.presence === 'missing') cancel = 'superseded-by-missing'
      if (cancel) { this.finish(key,'cancelled',cancel); continue }
      // The per-cycle event is already consumed. A small endpoint refinement or
      // threshold edit must not permanently discard an undelivered reminder.
      if (rule && (remainingUpperSeconds(observation,now) ?? Infinity) > rule.threshold) {
        if (entry.status !== 'paused' && entry.status !== 'failed') { entry.status='paused'; this.log('paused',key,'above-threshold') }
        continue
      }
      if (entry.status === 'paused') { entry.status='pending'; resumed=true; this.log('resumed',key) }
    }
    if (resumed && !this.speaking && this.timer === null) this.schedule()
  }
  retryFailed(): void {
    for (const entry of this.queue.values()) if (entry.status === 'failed') entry.status='paused'
    this.reconcile()
  }
  stop(): void {
    this.previewToken++; this.preparingChanged(false)
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    const wasSpeaking = this.speaking; this.speaking = null
    this.playbackChanged(false)
    for (const key of this.queue.keys()) this.log('cancelled',key,'monitor-stopped')
    this.queue.clear(); this.terminal.clear()
    wasSpeaking?.cancel()
  }
  cancelPendingPreview(): void {
    this.previewToken++; this.preparingChanged(false)
    if (this.speaking?.preview && !this.speaking.started) {
      const previous = this.speaking; this.speaking = null; previous.cancel(); this.playbackChanged(false)
    }
  }
  async preview(name = 'BUFF'): Promise<void> {
    if (this.speaking || [...this.queue.values()].some(entry => entry.status === 'pending')) return
    const token = ++this.previewToken, { locale } = this.current()
    if (token !== this.previewToken || this.speaking) return
    this.say(noticeText(name, locale))
  }
  private flush(): void {
    this.reconcile()
    if (this.speaking) return
    const { locale } = this.current()
    const entries = [...this.queue.entries()].filter(([,entry])=>entry.status==='pending')
      .sort((a,b)=>a[1].notice.order-b[1].notice.order)
    if (!entries.length) return
    const [key, entry] = entries[0]!
    this.say(noticeText(entry.notice.name, locale), key)
  }
  private say(text: string, key?: string): void {
    if (this.speaking) return
    const failure = (reason: string) => {
      if (key) { const entry=this.queue.get(key); if (entry) entry.status='failed'; this.log('failed',key,reason) }
      if (key) this.failed(reason)
      else this.previewFailed(reason)
    }
    const { preferences, locale } = this.current()
    const configuration = JSON.stringify([preferences.speech, locale])
    const job = { cancel: () => {}, preview: !key, started: false }
    this.speaking = job
    this.playbackChanged(true)
    const done = (error?: string) => {
      if (this.speaking !== job) return
      this.speaking = null
      this.preparingChanged(false)
      this.playbackChanged(false)
      if (error) failure(error)
      else if (key) this.finish(key,'completed')
      this.flush()
    }
    job.cancel = this.player.play({ text, locale, preferences: { ...preferences.speech }, preview: job.preview }, {
      preparing: value => { if (this.speaking === job && job.preview) this.preparingChanged(value) },
      valid: () => {
        this.reconcile()
        const current = this.current()
        if (this.speaking !== job || configuration !== JSON.stringify([current.preferences.speech, current.locale])) return false
        return !key || this.queue.get(key)?.status === 'pending' && noticeText(this.queue.get(key)!.notice.name, current.locale) === text
      },
      started: () => { job.started = true; if (key) { const entry = this.queue.get(key); if (entry) entry.status = 'speaking'; this.log('started', key) } },
      completed: () => done(), failed: done,
      cancelled: () => { if (this.speaking === job) { this.speaking = null; this.playbackChanged(false); this.schedule() } },
    })
  }
}
